import {
	type Config,
	PAID_PLAN_TIERS,
	PLAN_RANK,
	type PlanTier,
	config,
	getCreditPackByProductId,
} from "@repo/config";
import { toCurrentProductId } from "@repo/config/legacy-products";
import type { Purchase } from "@repo/database";

const plans = config.payments.plans as Config["payments"]["plans"];

type PlanId = keyof typeof config.payments.plans;
type PurchaseWithoutTimestamps = Omit<Purchase, "createdAt" | "updatedAt">;

function getActivePlanFromPurchases(purchases?: PurchaseWithoutTimestamps[]) {
	const now = Date.now();
	// 库里存量行的 productId 可能还是 Creem 的老 ID，这里统一翻成当前 env 的 ID，
	// 否则老用户的档位、去水印权限会在 env 切到 Waffo 后全部消失。
	purchases = purchases?.map((purchase) => ({
		...purchase,
		productId: toCurrentProductId(purchase.productId),
	}));

	// 首先查找状态为active或trialing的订阅
	// trialing 也视为"当前生效的订阅"：用户已完成结账、拥有 Purchase 记录，
	// 只是还在 3 天免费试用窗口内，UI 应展示其真实档位而不是回退到 Free。
	const activeSubscription = purchases?.find(
		(purchase) =>
			purchase.type === "SUBSCRIPTION" &&
			(purchase.status === "active" || purchase.status === "trialing"),
	);

	// 如果找到active订阅，返回它
	if (activeSubscription) {
		const plan = Object.entries(plans).find(([_, plan]) =>
			plan.prices?.some(
				(price) => price.productId === activeSubscription.productId,
			),
		);

		return {
			id: plan?.[0] as PlanId,
			price: plan?.[1].prices?.find(
				(price) => price.productId === activeSubscription.productId,
			),
			status: activeSubscription.status,
			purchaseId: activeSubscription.id,
			subscriptionId: activeSubscription.subscriptionId,
			interval: activeSubscription.interval || null,
			currentPeriodEndAt: activeSubscription.currentPeriodEndAt,
		};
	}

	// 如果没有active订阅，查找cancelled订阅，并按创建时间排序，返回最近的一个
	const cancelledSubscriptions = purchases
		?.filter((purchase) => {
			if (
				purchase.type !== "SUBSCRIPTION" ||
				purchase.status !== "cancelled"
			) {
				return false;
			}

			// cancel-at-period-end: 只有在周期结束之前，取消订阅仍然算“当前有效计划”
			if (!purchase.currentPeriodEndAt) {
				// 兼容旧数据：如果没有落库周期结束时间，保守起见仍视为当前计划
				return true;
			}

			const endAt = new Date(
				purchase.currentPeriodEndAt as any,
			).getTime();
			return Number.isFinite(endAt) && endAt > now;
		})
		.sort((a, b) => {
			// 由于我们移除了时间戳，这里我们可以使用ID比较
			// 一般情况下，ID越新创建时间越晚
			return a.id.localeCompare(b.id) * -1; // -1 使排序为降序（最新的在前）
		});

	// 如果找到cancelled订阅，返回最近的一个
	if (cancelledSubscriptions && cancelledSubscriptions.length > 0) {
		const latestCancelledSubscription = cancelledSubscriptions[0];
		const plan = Object.entries(plans).find(([_, plan]) =>
			plan.prices?.some(
				(price) =>
					price.productId === latestCancelledSubscription.productId,
			),
		);

		return {
			id: plan?.[0] as PlanId,
			price: plan?.[1].prices?.find(
				(price) =>
					price.productId === latestCancelledSubscription.productId,
			),
			status: latestCancelledSubscription.status,
			purchaseId: latestCancelledSubscription.id,
			subscriptionId: latestCancelledSubscription.subscriptionId,
			interval: latestCancelledSubscription.interval || null,
			currentPeriodEndAt: latestCancelledSubscription.currentPeriodEndAt,
		};
	}

	// 查找一次性购买（仅匹配能对应到某个 plan 的记录，例如买断制套餐；
	// 积分包等不在 plans 配置里的 ONE_TIME 购买应忽略，继续向下走 Free 兜底）
	const oneTimePurchase = purchases?.find(
		(purchase) =>
			purchase.type === "ONE_TIME" &&
			Object.values(plans).some((plan) =>
				plan.prices?.some(
					(price) => price.productId === purchase.productId,
				),
			),
	);

	if (oneTimePurchase) {
		const plan = Object.entries(plans).find(([_, plan]) =>
			plan.prices?.some(
				(price) => price.productId === oneTimePurchase.productId,
			),
		);

		return {
			id: plan?.[0] as PlanId,
			price: plan?.[1].prices?.find(
				(price) => price.productId === oneTimePurchase.productId,
			),
			status: "active",
			purchaseId: oneTimePurchase.id,
			subscriptionId: oneTimePurchase.subscriptionId,
			interval: oneTimePurchase.interval || null,
			currentPeriodEndAt: oneTimePurchase.currentPeriodEndAt,
		};
	}

	// 最后才返回免费计划
	const freePlan = Object.entries(plans).find(([_, plan]) => plan.isFree);

	return freePlan
		? {
				id: freePlan[0] as PlanId,
				status: "active",
				subscriptionId: null,
				interval: null, // 免费计划没有订阅周期
				currentPeriodEndAt: null,
			}
		: null;
}

export function createPurchasesHelper(
	rawPurchases: PurchaseWithoutTimestamps[],
) {
	const purchases = rawPurchases.map((purchase) => ({
		...purchase,
		productId: toCurrentProductId(purchase.productId),
	}));
	const activePlan = getActivePlanFromPurchases(purchases);

	const hasSubscription = (planIds?: PlanId[] | PlanId) => {
		return (
			!!activePlan &&
			(Array.isArray(planIds)
				? planIds.includes(activePlan.id)
				: planIds === activePlan.id)
		);
	};

	const hasPurchase = (planId: PlanId) => {
		return !!purchases?.some((purchase) =>
			Object.entries(plans)
				.find(([id]) => id === planId)?.[1]
				.prices?.some(
					(price) => price.productId === purchase.productId,
				),
		);
	};

	// 去水印权限：订阅任意付费计划，或曾经买过一次性积分包，均可永久去水印。
	// 积分包本身没有对应的 plan（不在 config.payments.plans 里），所以不能
	// 靠 activePlan.id 判断，需要单独查一次性购买记录。
	const purchasedPacks = purchases.flatMap((purchase) => {
		const pack =
			purchase.type === "ONE_TIME"
				? getCreditPackByProductId(purchase.productId)
				: undefined;
		return pack ? [pack] : [];
	});
	const hasCreditPackPurchase = purchasedPacks.length > 0;
	const canRemoveWatermark =
		hasSubscription([...PAID_PLAN_TIERS]) || hasCreditPackPurchase;

	// 能力按档位切（分辨率等）时用这个，而不是二元的 canRemoveWatermark。
	// 取订阅档和买过的包里最高 tier 两者的较高者（2026-09-29 起按包分档：
	// $9.90 包只到 Starter，其余包到 Pro）。Starter 订阅者再买 Pro 级包也能升上去。
	const subscriptionTier: PlanTier = PAID_PLAN_TIERS.includes(
		activePlan?.id as (typeof PAID_PLAN_TIERS)[number],
	)
		? (activePlan?.id as PlanTier)
		: "free";
	const planTier: PlanTier = purchasedPacks.reduce<PlanTier>(
		(best, pack) =>
			PLAN_RANK[pack.tier] > PLAN_RANK[best] ? pack.tier : best,
		subscriptionTier,
	);

	return {
		activePlan,
		hasSubscription,
		hasPurchase,
		canRemoveWatermark,
		planTier,
	};
}
